import React, { useEffect, useRef, useState, useCallback } from "react";
import { Bold, Italic, Underline, RemoveFormatting, Link2, Image as ImageIcon, Check, X } from "lucide-react";
import { toast } from "sonner";

const IMG_MAX_LATO = 1200;
const IMG_MAX_BYTE = 4 * 1024 * 1024;
const IMG_LARGH_PICCOLA = 180;
const TAGLIE = [
  { id: "12", label: "Piccolo", px: 12 },
  { id: "15", label: "Normale", px: 15 },
  { id: "18", label: "Medio", px: 18 },
  { id: "22", label: "Grande", px: 22 },
  { id: "28", label: "Molto grande", px: 28 },
];
const tagliaVicina = (px) => TAGLIE.reduce((a, b) => (Math.abs(b.px - px) < Math.abs(a.px - px) ? b : a)).id; // le immagini nel testo restano piccole: chi le riceve le apre a grandezza piena

const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
// Testo semplice → HTML dell'editor (una riga per <div>, righe vuote = <div><br></div>)
export const testoAHtml = (t) =>
  (t || "")
    .split("\n")
    .map((r) => `<div>${r ? esc(r) : "<br>"}</div>`)
    .join("");
// HTML dell'editor → testo semplice (innerText rispetta <br> e i blocchi; le immagini diventano [immagine])
export const htmlATesto = (html) => {
  const d = document.createElement("div");
  d.innerHTML = html || "";
  d.querySelectorAll("style,script").forEach((n) => n.remove());
  d.querySelectorAll("img").forEach((n) => n.replaceWith(document.createTextNode("[immagine]")));
  d.querySelectorAll("a[href]").forEach((n) => {
    if (n.textContent.trim() && n.textContent.trim() !== n.getAttribute("href")) n.append(document.createTextNode(` (${n.getAttribute("href")})`));
  });
  document.body.appendChild(d);
  const t = d.innerText;
  d.remove();
  return t.replace(/\u00a0/g, " ").replace(/\n{3,}/g, "\n\n").replace(/\s+$/, "");
};
export const htmlVuoto = (html) => !(html || "").replace(/<br\s*\/?>|<\/?div>|<\/?p>|&nbsp;|\s/gi, "").trim();

// Prima lettera del messaggio e dopo un punto (. ! ?) maiuscola, direttamente nel nodo di testo sotto al cursore
function maiuscolaAlCursore(root) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || !sel.isCollapsed) return;
  const n = sel.anchorNode;
  const off = sel.anchorOffset;
  if (!n || n.nodeType !== 3 || off < 1 || !root.contains(n)) return;
  const ch = n.data[off - 1];
  if (!/[a-zàèéìòù]/.test(ch)) return;
  const prima = n.data.slice(0, off - 1);
  let inizio;
  if (prima.trim() === "") {
    // inizio del nodo: guardo il testo che precede nell'editor (vuoto = inizio messaggio; "." = nuova frase)
    const r = document.createRange();
    r.setStart(root, 0);
    r.setEnd(n, 0);
    const prec = r.toString();
    inizio = prec.trim() === "" || /[.!?]\s*$/.test(prec);
  } else inizio = /[.!?]\s+$/.test(prima);
  if (!inizio) return;
  n.data = prima + ch.toUpperCase() + n.data.slice(off);
  const r2 = document.createRange();
  r2.setStart(n, off);
  r2.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r2);
}

// Immagine scelta → data URL ridotto (max 1200px, JPEG q0.85; PNG/GIF piccoli restano com'è)
function preparaImmagine(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = reject;
    r.onload = () => {
      const src = String(r.result);
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const k = Math.min(1, IMG_MAX_LATO / Math.max(img.width, img.height));
        if (k === 1 && file.size < 600 * 1024) return resolve(src);
        const c = document.createElement("canvas");
        c.width = Math.round(img.width * k);
        c.height = Math.round(img.height * k);
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL(file.type === "image/png" ? "image/png" : "image/jpeg", 0.85));
      };
      img.src = src;
    };
    r.readAsDataURL(file);
  });
}

// Editor di testo formattato (grassetto, corsivo, sottolineato, link, immagini) per il modulo Scrivi.
// `value` è HTML: viene riscritto nell'editor solo quando cambia dall'esterno (apertura, AI, traduzione).
export default function EditorRicco({ value, onChange, placeholder, editorRef, testid = "compose-body" }) {
  const innerRef = useRef(null);
  const ref = editorRef || innerRef;
  const fileRef = useRef(null);
  const rangeSalvato = useRef(null);
  const ultimoEmesso = useRef(null);
  const [stato, setStato] = useState({ bold: false, italic: false, underline: false });
  const [taglia, setTaglia] = useState("15");
  const tagliaPendente = useRef(15); // ultima altezza scelta: usata per convertire i <font size="7"> creati da Chrome mentre si scrive
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkTesto, setLinkTesto] = useState("");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (value !== ultimoEmesso.current && el.innerHTML !== (value || "")) {
      el.innerHTML = value || "";
      ultimoEmesso.current = value;
    }
  }, [value, ref]);

  const normalizzaFont = useCallback((el) => {
    el.querySelectorAll('font[size="7"]').forEach((f) => {
      const s = document.createElement("span");
      s.style.fontSize = tagliaPendente.current + "px";
      f.before(s);
      while (f.firstChild) s.appendChild(f.firstChild);
      f.remove();
    });
  }, []);

  const emetti = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    normalizzaFont(el);
    if (el.innerHTML === "<br>" || el.innerHTML === "<div><br></div>") el.innerHTML = "";
    const html = el.innerHTML;
    ultimoEmesso.current = html;
    onChange(html, htmlATesto(html));
  }, [onChange, ref, normalizzaFont]);

  const aggiornaStato = useCallback(() => {
    const el = ref.current;
    const sel = window.getSelection();
    if (!el || !sel || !sel.anchorNode || !el.contains(sel.anchorNode)) return;
    setStato({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
    });
    const n = sel.anchorNode;
    const elm = n.nodeType === 3 ? n.parentElement : n;
    if (elm && elm !== el) setTaglia(tagliaVicina(parseFloat(getComputedStyle(elm).fontSize) || 15));
    else setTaglia("15");
  }, [ref]);

  useEffect(() => {
    document.addEventListener("selectionchange", aggiornaStato);
    return () => document.removeEventListener("selectionchange", aggiornaStato);
  }, [aggiornaStato]);

  const salvaRange = () => {
    const sel = window.getSelection();
    const el = ref.current;
    if (sel && sel.rangeCount && el && el.contains(sel.anchorNode)) rangeSalvato.current = sel.getRangeAt(0).cloneRange();
    else rangeSalvato.current = null;
  };
  const ripristinaRange = () => {
    const el = ref.current;
    const sel = window.getSelection();
    if (!sel) return;
    const dentro = sel.rangeCount && el && el.contains(sel.anchorNode) && el.contains(sel.focusNode);
    el?.focus();
    if (dentro) return;
    sel.removeAllRanges();
    if (rangeSalvato.current) sel.addRange(rangeSalvato.current);
    else if (el) {
      const r = document.createRange();
      r.selectNodeContents(el);
      r.collapse(false);
      sel.addRange(r);
    }
  };

  const comando = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emetti();
    aggiornaStato();
  };

  // LINK: con testo selezionato lo trasforma in link, altrimenti inserisce "testo (o indirizzo)" come link
  const apriLink = () => {
    salvaRange();
    const sel = window.getSelection();
    setLinkTesto(sel && !sel.isCollapsed ? sel.toString() : "");
    setLinkUrl("");
    setLinkOpen(true);
  };
  const inserisciLink = () => {
    let url = linkUrl.trim();
    if (!url) return;
    if (!/^(https?:\/\/|mailto:)/i.test(url)) url = (url.includes("@") && !url.includes("/") ? "mailto:" : "https://") + url;
    ripristinaRange();
    const sel = window.getSelection();
    const testo = (linkTesto || "").trim() || url.replace(/^mailto:/, "");
    const selTesto = sel && !sel.isCollapsed ? sel.toString().trim() : "";
    if (selTesto && selTesto === testo) {
      document.execCommand("createLink", false, url);
      ref.current?.querySelectorAll("a").forEach((a) => { if (a.getAttribute("href") === url) a.setAttribute("target", "_blank"); });
    } else {
      document.execCommand("insertHTML", false, `<a href="${esc(url)}" target="_blank">${esc(testo)}</a>&nbsp;`);
    }
    setLinkOpen(false);
    emetti();
  };

  // IMMAGINE: dal dispositivo, ridotta e inserita nel testo (nella mail parte come immagine incorporata)
  const scegliImmagine = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Scegli un file immagine");
      return;
    }
    try {
      const src = await preparaImmagine(f);
      if (src.length > IMG_MAX_BYTE * 1.37) {
        toast.error("Immagine troppo grande (max 4 MB): riducila o inviala come allegato");
        return;
      }
      ripristinaRange();
      document.execCommand("insertHTML", false, `<img src="${src}" alt="${esc(f.name)}" width="${IMG_LARGH_PICCOLA}" style="width:${IMG_LARGH_PICCOLA}px;max-width:100%;height:auto" title="Clic per ingrandire/ridurre"><br>`);
      emetti();
    } catch (err) {
      toast.error("Immagine non leggibile");
    }
  };

  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const img = items.find((i) => i.type.startsWith("image/"));
    if (img) {
      e.preventDefault();
      const f = img.getAsFile();
      if (f) {
        salvaRange();
        scegliImmagine({ target: { files: [f], value: "" } });
      }
      return;
    }
    e.preventDefault();
    const t = (e.clipboardData || window.clipboardData).getData("text/plain");
    document.execCommand("insertText", false, t);
  };

  const onInput = (e) => {
    if (e.nativeEvent?.inputType === "insertText") maiuscolaAlCursore(ref.current);
    emetti();
  };

  const onKeyDown = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    const k = e.key.toLowerCase();
    if (k === "b" || k === "i" || k === "u") {
      e.preventDefault();
      comando({ b: "bold", i: "italic", u: "underline" }[k]);
    } else if (k === "k") {
      e.preventDefault();
      apriLink();
    }
  };

  // clic su un'immagine inserita: alterna piccola ↔ grande
  const onClickArea = (e) => {
    const img = e.target;
    if (img?.tagName !== "IMG") return;
    const piccola = (img.getAttribute("width") || "") !== "";
    if (piccola) {
      img.removeAttribute("width");
      img.style.width = "";
      img.style.maxWidth = "100%";
    } else {
      img.setAttribute("width", String(IMG_LARGH_PICCOLA));
      img.style.width = IMG_LARGH_PICCOLA + "px";
    }
    emetti();
  };

  const pd = (e) => e.preventDefault();
  const attivo = (v) => (v ? "on" : "");

  // ALTEZZA CARATTERE: execCommand fontSize (1–7) → <font size="7"> convertito in <span style="font-size:Npx">
  const cambiaTaglia = (px) => {
    setTaglia(String(px));
    tagliaPendente.current = px;
    const el = ref.current;
    if (!el) return;
    ripristinaRange();
    document.execCommand("fontSize", false, "7");
    emetti();
  };

  return (
    <div className="editor-ricco" data-testid="editor-ricco">
      <div className="editor-toolbar" role="toolbar" aria-label="Formattazione" data-testid="editor-toolbar">
        <button type="button" className={`editor-tool ${attivo(stato.bold)}`} onMouseDown={pd} onClick={() => comando("bold")} title="Grassetto (Ctrl+B)" aria-label="Grassetto" aria-pressed={stato.bold} data-testid="editor-bold">
          <Bold size={17} strokeWidth={2.6} />
        </button>
        <button type="button" className={`editor-tool ${attivo(stato.italic)}`} onMouseDown={pd} onClick={() => comando("italic")} title="Corsivo (Ctrl+I)" aria-label="Corsivo" aria-pressed={stato.italic} data-testid="editor-italic">
          <Italic size={17} strokeWidth={2.2} />
        </button>
        <button type="button" className={`editor-tool ${attivo(stato.underline)}`} onMouseDown={pd} onClick={() => comando("underline")} title="Sottolineato (Ctrl+U)" aria-label="Sottolineato" aria-pressed={stato.underline} data-testid="editor-underline">
          <Underline size={17} strokeWidth={2.2} />
        </button>
        <span className="editor-sep" />
        <select
          className="editor-taglia"
          value={taglia}
          onMouseDown={salvaRange}
          onChange={(e) => cambiaTaglia(Number(e.target.value))}
          title="Altezza carattere"
          aria-label="Altezza carattere"
          data-testid="editor-taglia"
        >
          {TAGLIE.map((t) => (
            <option key={t.id} value={t.id}>{t.label} · {t.px}</option>
          ))}
        </select>
        <span className="editor-sep" />
        <button type="button" className={`editor-tool ${attivo(linkOpen)}`} onMouseDown={pd} onClick={apriLink} title="Inserisci link (Ctrl+K)" aria-label="Inserisci link" data-testid="editor-link">
          <Link2 size={18} strokeWidth={2.2} />
        </button>
        <button type="button" className="editor-tool" onMouseDown={pd} onClick={() => { salvaRange(); fileRef.current?.click(); }} title="Inserisci immagine" aria-label="Inserisci immagine" data-testid="editor-image">
          <ImageIcon size={18} strokeWidth={2.2} />
        </button>
        <input type="file" accept="image/*" ref={fileRef} onChange={scegliImmagine} style={{ display: "none" }} data-testid="editor-image-input" />
        <span className="editor-sep" />
        <button type="button" className="editor-tool" onMouseDown={pd} onClick={() => comando("removeFormat")} title="Togli formattazione" aria-label="Togli formattazione" data-testid="editor-clear">
          <RemoveFormatting size={17} strokeWidth={2.2} />
        </button>
      </div>
      {linkOpen && (
        <div className="editor-link-box" data-testid="editor-link-box">
          <input
            className="wf-input editor-link-input"
            placeholder="Indirizzo: https://… oppure nome@email.it"
            value={linkUrl}
            autoFocus
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") inserisciLink();
              if (e.key === "Escape") setLinkOpen(false);
            }}
            data-testid="editor-link-url"
          />
          <input
            className="wf-input editor-link-input"
            placeholder="Testo da mostrare (facoltativo)"
            value={linkTesto}
            onChange={(e) => setLinkTesto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") inserisciLink();
              if (e.key === "Escape") setLinkOpen(false);
            }}
            data-testid="editor-link-text"
          />
          <button type="button" className="editor-tool editor-link-ok" onClick={inserisciLink} disabled={!linkUrl.trim()} title="Inserisci" aria-label="Inserisci link" data-testid="editor-link-ok">
            <Check size={18} strokeWidth={2.8} />
          </button>
          <button type="button" className="editor-tool" onClick={() => setLinkOpen(false)} title="Annulla" aria-label="Annulla" data-testid="editor-link-cancel">
            <X size={18} strokeWidth={2.4} />
          </button>
        </div>
      )}
      <div
        ref={ref}
        className="wf-textarea compose-body-auto editor-area"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Messaggio"
        data-placeholder={placeholder}
        onInput={onInput}
        onPaste={onPaste}
        onKeyDown={onKeyDown}
        onClick={onClickArea}
        onKeyUp={aggiornaStato}
        onMouseUp={aggiornaStato}
        onFocus={aggiornaStato}
        spellCheck
        data-testid={testid}
      />
    </div>
  );
}
